import React, { Fragment } from 'react';
import Dash from '@dashboard/components/Dash';
import { bumpOption, compactDetail, productPriceOptions, ticketOption } from './couponPrices';
import { describeCouponRules, resolveCouponRule } from './couponRuleSentence';
import { isEvent, takesBumps, takesItems, WHOLE_ORDER } from './scopePage';
import { usePageBumps, usePagePrices, usePageTickets } from './useScopeOptions';

export const SCOPE_LIST_HEADER = 'Works on any of these:';

export const CouponScopeName = ({ page }) => (
  <>
    <div className="text-xs text-muted-foreground">{isEvent(page) ? 'Event' : 'Checkout'}</div>
    <div className="font-medium text-foreground break-words">{page.name}</div>
  </>
);

export const CouponScopePage = ({
  page,
  appliesToSetupFee,
  appliesToTicketFees,
  nameAs: Name = 'div',
  sentenceAs: Sentence = 'p',
}) => (
  <div className="min-w-0 text-left">
    <Name>
      <CouponScopeName page={page} />
    </Name>
    <Sentence className="mt-1 break-words text-foreground">
      <CouponRulesSentence
        page={page}
        rules={page.rules}
        appliesToSetupFee={appliesToSetupFee}
        appliesToTicketFees={appliesToTicketFees}
      />
    </Sentence>
  </div>
);

export const SentenceParts = ({ parts }) =>
  parts.map((part, index) =>
    part.bold ? (
      <strong key={index} className="font-semibold">
        {part.text}
      </strong>
    ) : (
      <Fragment key={index}>{part.text}</Fragment>
    ),
  );

/** A page's rules (form shape: `{ appliesTo, itemIds, bumpIds }`) as one sentence. */
export const CouponRulesSentence = ({ page, rules, appliesToSetupFee, appliesToTicketFees }) => {
  const event = isEvent(page);
  const limited = !rules.some(rule => rule.appliesTo === WHOLE_ORDER);
  const needItems = limited && rules.some(rule => takesItems(rule.appliesTo));
  const needBumps =
    limited && rules.some(rule => takesBumps(rule.appliesTo) && rule.bumpIds.length > 0);

  const prices = usePagePrices(page, { enabled: needItems && !event });
  const tickets = usePageTickets(page, { enabled: needItems && event });
  const bumps = usePageBumps(page, { enabled: needBumps });
  if (prices.isLoading || tickets.isLoading || bumps.isLoading) return <Dash />;

  const pickedItemIds = rules.flatMap(rule => rule.itemIds);
  const itemOptions = event
    ? tickets.ticketTypes.map(ticket => ticketOption(ticket, page.eventDetails?.currency))
    : productPriceOptions(prices.product, pickedItemIds);
  const bumpOptions = (bumps.orderBumps ?? []).map(bumpOption);

  const parts = describeCouponRules({
    kind: event ? 'event' : 'checkout',
    rules: rules.map(rule =>
      resolveCouponRule(rule, {
        itemOptions: itemOptions.map(compactDetail),
        bumpOptions: bumpOptions.map(compactDetail),
      }),
    ),
    appliesToSetupFee,
    appliesToTicketFees,
  });
  return <SentenceParts parts={parts} />;
};
